import { Image } from '@unpic/react';
import { useEffect, useState } from 'react';
import { ApplicantAnswersSchema } from '@/apply/answers';
import logo from '@/assets/images/logo.webp';
import { EnhanceStep } from '@/components/onboarding/enhance/EnhanceStep';
import { LinkedInStep } from '@/components/onboarding/LinkedInStep';
import { ProfileStep } from '@/components/onboarding/ProfileStep';
import { UploadStep } from '@/components/onboarding/UploadStep';
import { Spinner } from '@/components/ui/Spinner';
import { Stepper } from '@/components/ui/Stepper';
import { useExtractEmploymentHistory } from '@/hooks/useExtractEmploymentHistory';
import { useRunCycle } from '@/hooks/useRunCycle';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import type { EmploymentEntry } from '@/lib/employment-extractor';

const ONBOARDING_STEPS = [
  { id: 'upload', label: 'Resume' },
  { id: 'profile', label: 'Profile' },
  { id: 'enhance', label: 'Enhance' },
  { id: 'linkedin', label: 'LinkedIn' },
] as const;

type Step = (typeof ONBOARDING_STEPS)[number]['id'];

interface OnboardingProps {
  onComplete: () => void;
}

export const Onboarding = ({ onComplete }: OnboardingProps) => {
  const [step, setStep] = useState<Step>('upload');
  const [resumeText, setResumeText] = useState('');
  const [resumeFilePath, setResumeFilePath] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [profileData, setProfileData] = useState<Record<string, unknown>>({});
  const [resumeMd, setResumeMd] = useState('');
  const [linkedinCookie, setLinkedinCookie] = useState('');
  const [extractedEmploymentHistory, setExtractedEmploymentHistory] = useState<
    EmploymentEntry[]
  >([]);

  const updateProfile = useUpdateProfile();
  const runCycle = useRunCycle();
  const extractEmploymentHistory = useExtractEmploymentHistory();

  // Fire-and-store: extract employment history as soon as resume text is set.
  // Runs only when resumeText transitions from empty to non-empty.
  // biome-ignore lint/correctness/useExhaustiveDependencies: extractEmploymentHistory.mutate is a stable useMutation reference; including it would cause duplicate requests on every render
  useEffect(() => {
    if (!resumeText) return;
    extractEmploymentHistory.mutate(resumeText, {
      onSuccess: (data) =>
        setExtractedEmploymentHistory(data.employmentHistory),
    });
  }, [resumeText]);

  const currentStepIndex = ONBOARDING_STEPS.findIndex((s) => s.id === step);

  const finish = (cookie: string) => {
    const payload = {
      ...profileData,
      application_answers: JSON.stringify(
        ApplicantAnswersSchema.parse({
          employmentHistory: extractedEmploymentHistory,
        }),
      ),
      linkedin_cookie: cookie || linkedinCookie || null,
      resume_file_name: resumeFileName || null,
      resume_file_path: resumeFilePath || null,
      resume_md: resumeMd || null,
      resume_raw: resumeText,
    };
    updateProfile.mutate(payload, {
      onSuccess: () => {
        runCycle.mutate();
        onComplete();
      },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="flex flex-col items-center justify-center gap-12">
        <Image alt="Logo" height={300} src={logo} width={300} />
        <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
          {/* Step progress indicator */}
          <div className="border-b border-border px-6 py-3.5">
            <Stepper current={currentStepIndex} steps={[...ONBOARDING_STEPS]} />
          </div>

          <div className="p-6">
            {step === 'upload' && (
              <UploadStep
                onNext={(text, filePath, fileName) => {
                  setResumeText(text);
                  setResumeFilePath(filePath);
                  setResumeFileName(fileName);
                  setStep('profile');
                }}
              />
            )}

            {step === 'profile' && (
              <ProfileStep
                initialData={profileData}
                onBack={() => setStep('upload')}
                onNext={(data) => {
                  setProfileData(data);
                  setStep('enhance');
                }}
                resumeText={resumeText}
              />
            )}

            {step === 'enhance' && (
              <EnhanceStep
                onBack={() => setStep('profile')}
                onNext={(md) => {
                  setResumeMd(md);
                  setStep('linkedin');
                }}
                onSkip={() => setStep('linkedin')}
                resumeText={resumeText}
              />
            )}

            {step === 'linkedin' && (
              <LinkedInStep
                onBack={() => setStep('enhance')}
                onNext={(cookie) => {
                  setLinkedinCookie(cookie);
                  finish(cookie);
                }}
                onSkip={() => finish('')}
              />
            )}

            {updateProfile.isPending && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <Spinner label="" />
                <p className="text-xs lg:text-base text-foreground-muted">
                  Saving profile...
                </p>
              </div>
            )}

            {updateProfile.isError && (
              <p className="mt-4 text-center text-xs lg:text-base text-score-low">
                Failed to save profile: {updateProfile.error.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
